import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  FileText, Search, Calendar, Loader2, Download, Printer, Eye,
  Plus, Building2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { SellerDashboardLayout } from "./Dashboard";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const InvoicesPage = () => {
  const { t, i18n } = useTranslation("seller");
  const { t: tn } = useTranslation("nav");
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("invoices");
  const [sales, setSales] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes] = await Promise.all([
        apiClient.get("/pos/sales?period=all")
      ]);
      // Handle both direct array or {sales: []} response
      const salesData = Array.isArray(salesRes.data) ? salesRes.data : (salesRes.data.sales || []);
      setSales(salesData);
      // Proformas would be fetched here too if endpoint exists
    } catch (error) {
      console.error("Error fetching data:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: 'CDF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handlePrintInvoice = (saleId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pos/invoice/${saleId}/pdf`, '_blank');
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setShowDetailDialog(true);
  };

  const filteredSales = sales.filter(sale => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      sale.invoice_number?.toLowerCase().includes(query) ||
      sale.client_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <SellerDashboardLayout title={tn("seller.invoices")} >
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
        </div>
      </SellerDashboardLayout>
    );
  }

  return (
    <SellerDashboardLayout title={t("invoices.pageTitle")} >
      {/* Search */}
      <Card className="border-0 shadow-soft mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("invoices.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-invoices"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t("invoices.tabInvoices", { count: filteredSales.length })}
          </TabsTrigger>
          <TabsTrigger value="proformas" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t("invoices.tabProformas", { count: proformas.length })}
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="border-0 shadow-soft">
            <CardContent className="p-0">
              {filteredSales.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">{t("invoices.empty")}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("invoices.colInvoice")}</TableHead>
                        <TableHead>{t("invoices.colDate")}</TableHead>
                        <TableHead>{t("invoices.colClient")}</TableHead>
                        <TableHead className="text-right">{t("invoices.colAmount")}</TableHead>
                        <TableHead className="text-right">{t("invoices.colActions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSales.map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell className="font-medium">
                            {sale.invoice_number}
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {formatDate(sale.created_at)}
                          </TableCell>
                          <TableCell>
                            {sale.client_name || (
                              <span className="text-slate-400">{t("invoices.anonymous")}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-omu-blue-600">
                            {formatCurrency(sale.total)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewItem(sale)}
                                title={t("invoices.view")}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePrintInvoice(sale.id)}
                                title={t("invoices.downloadPdf")}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePrintInvoice(sale.id)}
                                title={t("invoices.print")}
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Proformas Tab */}
        <TabsContent value="proformas">
          <Card className="border-0 shadow-soft">
            <CardContent className="p-0">
              {proformas.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">{t("invoices.proformasEmpty")}</p>
                  <p className="text-sm text-slate-400 mt-2">
                    {t("invoices.proformasHint")}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("invoices.colProforma")}</TableHead>
                        <TableHead>{t("invoices.colDate")}</TableHead>
                        <TableHead>{t("invoices.colClient")}</TableHead>
                        <TableHead className="text-right">{t("invoices.colAmount")}</TableHead>
                        <TableHead className="text-right">{t("invoices.colActions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {proformas.map((proforma) => (
                        <TableRow key={proforma.id}>
                          <TableCell className="font-medium">
                            {proforma.proforma_number}
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {formatDate(proforma.created_at)}
                          </TableCell>
                          <TableCell>
                            {proforma.client_name || "-"}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(proforma.total)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-omu-blue-500" />
              {t("invoices.invoiceTitle", { number: selectedItem?.invoice_number })}
            </DialogTitle>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex justify-between items-start p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-lg">{selectedItem.invoice_number}</p>
                  <p className="text-sm text-slate-500">{formatDate(selectedItem.created_at)}</p>
                </div>
                <Badge className="bg-green-100 text-green-700">{t("invoices.paid")}</Badge>
              </div>

              {/* Client */}
              {selectedItem.client_name && (
                <div className="p-3 border rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">{t("sales.client")}</p>
                  <p className="font-medium">{selectedItem.client_name}</p>
                  {selectedItem.client_phone && (
                    <p className="text-sm text-slate-600">{selectedItem.client_phone}</p>
                  )}
                </div>
              )}

              {/* Items */}
              <div>
                <p className="text-sm text-slate-500 mb-2">{t("invoices.articles")}</p>
                <div className="border rounded-lg divide-y">
                  {selectedItem.items?.map((item, i) => (
                    <div key={i} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{item.medicine_name}</p>
                        <p className="text-sm text-slate-500">
                          {formatCurrency(item.unit_price)} x {item.quantity}
                        </p>
                      </div>
                      <p className="font-semibold">
                        {formatCurrency(item.unit_price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center p-4 bg-omu-blue-50 rounded-lg">
                <span className="text-lg font-semibold">{t("invoices.totalTtc")}</span>
                <span className="text-2xl font-bold text-omu-blue-600">
                  {formatCurrency(selectedItem.total)}
                </span>
              </div>

              {/* Footer note */}
              <p className="text-xs text-slate-500 text-center">
                {t("invoices.thankYou")}
              </p>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
              className="w-full sm:w-auto"
            >
              {t("actions.close")}
            </Button>
            <Button
              className="w-full sm:w-auto bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={() => selectedItem && handlePrintInvoice(selectedItem.id)}
            >
              <Printer className="h-4 w-4 mr-2" />
              {t("invoices.printDownload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerDashboardLayout>
  );
};

export default InvoicesPage;
